// Licenses page (ADR-179 P2 / D5-P2). ListMyLicenses (account forced
// server-side, no request field) + a per-license DownloadMyLicense action.
// Read-only: NO issue/suspend/resume/revoke controls — license lifecycle
// mutation stays operator-only (ADR-179 D2.3), this surface cannot reach
// those RPCs at all (they are not even imported here, let alone wired to a
// customer_session-authenticated call).
//
// Three-surface UI alignment pass (three-surface-ui-alignment): restyled
// onto the shared genesis.ts tokens + glass-card idiom, matching the
// operator console's licenses.tsx and the ui/ app shell.
import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import * as pb from '@/generated/console_pb'
import { listMyLicenses, downloadMyLicense } from '@/lib/auth/customer-api'
import { billingStateLabel, deploymentTypeLabel, licenseStatusLabel, licenseTierLabel } from '@/lib/formatters'
import { DANGER, DARK, GOLD, GREY_300, GREY_400, GREY_500, SUCCESS, WARNING, WHITE, errorBlockStyle } from '@/lib/genesis'

export function PortalLicensesPage() {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['portal-my-licenses'],
    queryFn: () => listMyLicenses(),
  })

  return (
    <div>
      <h1 className="font-display mb-4 text-xl font-semibold" style={{ color: WHITE }}>Licenses</h1>

      {isLoading && <p className="text-sm" style={{ color: GREY_400 }}>Loading licenses…</p>}
      {isError && (
        <div className="p-3" style={errorBlockStyle}>
          <p className="text-sm" style={{ color: WHITE }}>Failed to load licenses.</p>
        </div>
      )}
      {!isLoading && !isError && data && data.getLicensesList().length === 0 && (
        <p className="text-sm" style={{ color: GREY_400 }}>No licenses on this account yet.</p>
      )}

      {!isLoading && !isError && data && data.getLicensesList().length > 0 && (
        <div className="flex flex-col gap-3">
          {data.getLicensesList().map((lic) => (
            <LicenseCard key={lic.getId()} license={lic} />
          ))}
        </div>
      )}
    </div>
  )
}

function LicenseCard({ license }: { license: pb.License }) {
  const [downloading, setDownloading] = useState(false)
  const [downloadError, setDownloadError] = useState<string | null>(null)

  async function handleDownload() {
    setDownloading(true)
    setDownloadError(null)
    try {
      const res = await downloadMyLicense(license.getId())
      const lic = res.getLicense()
      if (!lic) {
        throw new Error('Server returned no license data.')
      }
      const payload = {
        licenseId: lic.getId(),
        tier: licenseTierLabel(lic.getTier()),
        seatLimit: lic.getSeatLimit(),
        signedToken: lic.getSignedToken(),
        publicKeyFingerprint: lic.getPublicKeyFingerprint(),
        expiresAt: lic.getExpiresAt()
          ? new Date(
              (lic.getExpiresAt()?.getSeconds() ?? 0) * 1000 +
                (lic.getExpiresAt()?.getNanos() ?? 0) / 1e6,
            ).toISOString()
          : null,
      }
      const blob = new Blob([JSON.stringify(payload, null, 2)], {
        type: 'application/json',
      })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `atf-license-${lic.getId()}.json`
      document.body.appendChild(a)
      a.click()
      a.remove()
      URL.revokeObjectURL(url)
    } catch (err) {
      setDownloadError(err instanceof Error ? err.message : 'Download failed.')
    } finally {
      setDownloading(false)
    }
  }

  const status = license.getStatus()

  return (
    <div className="glass-card p-4">
      <div className="mb-2 flex items-center justify-between">
        <span className="font-mono text-xs" style={{ color: GREY_500 }}>{license.getId()}</span>
        <div className="flex gap-1.5">
          <Chip label={licenseTierLabel(license.getTier())} />
          <Chip label={billingStateLabel(license.getBillingState())} />
          <Chip label={deploymentTypeLabel(license.getDeploymentType())} />
          <Chip
            label={licenseStatusLabel(status)}
            tone={
              status === pb.LicenseStatus.LICENSE_STATUS_ACTIVE
                ? 'green'
                : status === pb.LicenseStatus.LICENSE_STATUS_SUSPENDED
                  ? 'amber'
                  : status === pb.LicenseStatus.LICENSE_STATUS_REVOKED
                    ? 'red'
                    : 'neutral'
            }
          />
        </div>
      </div>
      <div className="mb-3 text-sm" style={{ color: GREY_400 }}>
        instance: <span className="font-mono">{license.getInstanceId() || 'unassigned'}</span>
        {' · '}
        seats: {license.getSeatLimit() === 0 ? 'unlimited' : license.getSeatLimit()}
      </div>

      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={handleDownload}
          disabled={downloading}
          className="rounded-lg px-2.5 py-1 text-xs disabled:opacity-40"
          style={{ border: `1px solid rgba(212, 184, 92, 0.6)`, color: GOLD }}
        >
          {downloading ? 'Preparing…' : 'Download'}
        </button>
        {downloadError && (
          <span className="rounded-lg px-2 py-1 text-xs" style={{ backgroundColor: DARK, borderLeft: `3px solid ${DANGER}`, color: WHITE }}>
            {downloadError}
          </span>
        )}
      </div>
    </div>
  )
}

function Chip({ label, tone = 'neutral' }: { label: string; tone?: 'neutral' | 'green' | 'amber' | 'red' }) {
  const toneStyle: Record<string, React.CSSProperties> = {
    neutral: { backgroundColor: DARK, color: GREY_300 },
    green: { backgroundColor: 'rgba(74, 222, 128, 0.12)', color: SUCCESS },
    amber: { backgroundColor: 'rgba(251, 191, 36, 0.12)', color: WARNING },
    red: { backgroundColor: 'rgba(242, 123, 118, 0.12)', color: DANGER },
  }

  return (
    <span className="rounded-full px-2 py-0.5 text-xs" style={toneStyle[tone]}>
      {label}
    </span>
  )
}
